import { OvCard, OvForm, OvFormInput } from '@components'
import { LABELS, TOOLTIPS } from '@constants'
import { useStore } from '@hooks/useStore'
import { Add, Delete, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { EmailTemplateForm } from '../email'

export const InvoiceSettingsForm = observer(() => {
  const FormInput = OvFormInput<any>
  const { email } = useStore()
  const [formOpen, setFormOpen] = useState(false)
  const [editingTemplateId, setEditingTemplateId] = useState<number | undefined>(undefined)

  useEffect(() => {
    email.getTemplates()
    return () => email.reset()
  }, [])

  const invoiceTemplates = email.templates_list.filter(template => template.type === 'invoice')

  const handleCreate = () => {
    setEditingTemplateId(undefined)
    setFormOpen(true)
  }

  const handleEdit = (id: number) => {
    setEditingTemplateId(id)
    setFormOpen(true)
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('Weet je zeker dat je dit sjabloon wilt verwijderen?')) {
      try {
        await email.deleteTemplate(id)
      } catch (error) {
        console.error('Error deleting template:', error)
      }
    }
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setEditingTemplateId(undefined)
  }

  const handleFormSuccess = async () => {
    await email.getTemplates()
  }

  return (
    <Stack spacing={4}>
      <OvForm>
        <Stack
          direction="column"
          gap={2}>
          <Typography variant="h4">Algemeen</Typography>
          <FormInput
            label="Factuur nummer format"
            name="company.settings.invoice.nr_format"
            defaultValue="[year][number]"
            helperText="Bijv. 20250001 [year][number]"
          />
          <FormInput
            label="Factuurnummer begint bij"
            name="company.settings.invoice.nr_start"
            type="number"
            helperText="Let op: Outvoice genereert automatisch factuurnummers volgens bovenstaand format.
            Als je dit boekjaar al facturen hebt verstuurd voor je Outvoice hebt gebruikt, dien je hier het
            het eerstvolgende factuurnummer (bijv. 15) in te vullen. Outvoice begint dan met tellen vanaf dit nummer.
            Gebruik dit dus alleen als je al facturen hebt gestuurd."
            inputProps={{
              type: 'number',
              min: 1,
              step: 1,
            }}
          />
          <FormInput
            label={`${LABELS.INVOICE_EXPIRES_AFTER}`}
            name="company.invoice_expires_after"
            inputProps={{
              type: 'number',
              min: 0,
              step: 1,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">dagen</InputAdornment>,
            }}
          />
        </Stack>
      </OvForm>

      <Divider />

      {/* Email Templates Section */}
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}>
          <Typography variant="h4">E-mailsjablonen</Typography>
          <Tooltip title={TOOLTIPS.CREATE_EMAIL_TEMPLATE}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleCreate}>
              Nieuw sjabloon
            </Button>
          </Tooltip>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            {email.loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>Laden...</Typography>
              </Box>
            ) : invoiceTemplates.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Geen factuur e-mailsjablonen gevonden</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}>
                  Klik op &apos;Nieuw sjabloon&apos; om een e-mailsjabloon aan te maken
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Naam</TableCell>
                      <TableCell>Onderwerp</TableCell>
                      <TableCell>Standaard</TableCell>
                      <TableCell align="right">Acties</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceTemplates.map(template => (
                      <TableRow
                        key={template.id}
                        hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="medium">
                            {template.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary">
                            {template.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {template.is_default && (
                            <Chip
                              label="Standaard"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end">
                            <Tooltip title={TOOLTIPS.EDIT_EMAIL_TEMPLATE}>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(template.id)}
                                title="Bewerken">
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={TOOLTIPS.DELETE_EMAIL_TEMPLATE}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(template.id)}
                                title="Verwijderen"
                                color="error">
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <EmailTemplateForm
          open={formOpen}
          onClose={handleFormClose}
          templateId={editingTemplateId}
          initialType="invoice"
          onSuccess={handleFormSuccess}
        />
      </Box>
    </Stack>
  )
})
